import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by showing the matching media immediately,
   * then syncing thumbnails from the section response.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const mediaId = event.detail?.mediaId || this.#selectedOptionMediaId(event.target);
    if (mediaId) this.#showMedia(mediaId);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const selectedMediaId =
          mediaId || newMediaGallery.querySelector('[data-media-id]')?.getAttribute('data-media-id') || '';
        const alreadyPresent = selectedMediaId
          ? Boolean(this.querySelector(`[data-media-id="${selectedMediaId}"]`))
          : false;

        if (alreadyPresent) {
          this.#showMedia(selectedMediaId);
          this.#syncControls(newMediaGallery);
          return;
        }

        morph(this, newMediaGallery, MORPH_OPTIONS);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * @param {Element} picker
   * @returns {string}
   */
  #selectedOptionMediaId(picker) {
    const selected =
      picker.querySelector('[data-current-checked="true"]') ||
      picker.querySelector('input:checked') ||
      picker.querySelector('option[selected]');

    return selected instanceof HTMLElement ? selected.dataset.mediaId || '' : '';
  }

  /**
   * Select the matching media in place. Keep featured-first order and leave every image visible.
   * @param {string} mediaId
   */
  #showMedia(mediaId) {
    if (!mediaId || !this.slideshow) return;

    const id = String(mediaId);

    queueMicrotask(() => {
      this.slideshow?.select({ id }, undefined, { animate: false });
    });
  }

  /**
   * Keep the zoom dialog in sync without remounting the main gallery.
   * @param {Element} newMediaGallery
   */
  #syncControls(newMediaGallery) {
    const currentZoom = this.querySelector('zoom-dialog-component');
    const nextZoom = newMediaGallery.querySelector('zoom-dialog-component');
    if (currentZoom && nextZoom) morph(currentZoom, nextZoom, MORPH_OPTIONS);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Opens zoom for the currently selected carousel slide (Optimal7-style zoom button).
   *
   * @param {PointerEvent} event - The pointer event.
   */
  zoomCurrent(event) {
    const index = this.slideshow?.current ?? 0;
    const slide = this.slideshow?.slides?.[index];
    const zoomEvent =
      slide instanceof Element
        ? {
            preventDefault() {},
            target: slide,
          }
        : event;

    this.zoom(index, /** @type {PointerEvent} */ (zoomEvent));
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
